import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { parseSettingNumber } from '@/lib/systemSettings';

// Define all limit keys (without _free/_premium suffix)
const LIMIT_KEYS = [
  'ai_questions_limit',
  'name_swipes_limit',
  'checklists_limit',
  'all_checklist_templates',
  'partner_connection',
  'barcode_scanner',
  'pregnancy_card',
  'contraction_partner_notifications',
  'baby_names_assign',
  // Baby Care limits
  'baby_care_history_days',
  'baby_care_stats_max_days', // Max days for stats: 1=today, 7, 14, 30, 180=individual
  'baby_care_feeding',
  'baby_care_diaper',
  'baby_care_sleep',
  'baby_care_medication',
  'baby_care_temperature',
  'baby_care_notes',
  // Baby Growth limits
  'baby_growth_entries_max',
  // Sleep Plan limits
  'sleep_plan_max_children',
  'sleep_plan_analysis_days',
  'sleep_plan_notifications',
  'sleep_plan_patterns',
  // Custom Colors
  'custom_colors',
] as const;

type LimitKey = typeof LIMIT_KEYS[number];

// Default values if no settings are configured
const DEFAULT_LIMITS: Record<string, { free: number | boolean | string; premium: number | boolean | string }> = {
  ai_questions_limit: { free: 10, premium: -1 },
  name_swipes_limit: { free: 50, premium: -1 },
  checklists_limit: { free: 3, premium: -1 },
  all_checklist_templates: { free: false, premium: true },
  partner_connection: { free: false, premium: true },
  barcode_scanner: { free: false, premium: true },
  pregnancy_card: { free: false, premium: true },
  contraction_partner_notifications: { free: 'off', premium: 'all' },
  baby_names_assign: { free: 0, premium: 3 },
  // Baby Care limits
  baby_care_history_days: { free: 7, premium: -1 },
  baby_care_stats_max_days: { free: 1, premium: 180 }, // 1=today, 7, 14, 30, 180=individual (max 6 months)
  baby_care_feeding: { free: true, premium: true },
  baby_care_diaper: { free: true, premium: true },
  baby_care_sleep: { free: true, premium: true },
  baby_care_medication: { free: false, premium: true },
  baby_care_temperature: { free: false, premium: true },
  baby_care_notes: { free: false, premium: true },
  // Baby Growth limits
  baby_growth_entries_max: { free: 3, premium: -1 },
  // Sleep Plan limits
  sleep_plan_max_children: { free: 0, premium: -1 }, // 0=not available for free, -1=unlimited for premium
  sleep_plan_analysis_days: { free: 1, premium: 30 }, // 1=today only for free, 30 days for premium
  sleep_plan_notifications: { free: true, premium: true },
  sleep_plan_patterns: { free: false, premium: true }, // Pattern recognition premium only
  // Custom Colors
  custom_colors: { free: false, premium: true }, // Custom color scheme
};

// Spalten, die fuer die Premium-Entscheidung gebraucht werden
const PREMIUM_FIELDS =
  'is_lifetime, is_beta_tester, subscription_tier, premium_expires_at, free_premium_until, premium_shared_from';

interface PremiumProfile {
  is_lifetime: boolean | null;
  is_beta_tester: boolean | null;
  subscription_tier: string | null;
  premium_expires_at: string | null;
  free_premium_until: string | null;
  premium_shared_from: string | null;
}

function isInFuture(timestamp: string | null): boolean {
  if (!timestamp) return false;
  const ms = new Date(timestamp).getTime();
  return !Number.isNaN(ms) && ms > Date.now();
}

/**
 * Premium-Bedingungen eines einzelnen Accounts, ohne Partner-Freigabe.
 *
 * Gegenstueck: has_own_premium() in
 * supabase/migrations/160_swipe_limit_enforcement.sql - beide Seiten muessen
 * dieselben Ergebnisse liefern, Aenderungen also immer paarweise.
 */
function hasOwnPremium(profile: PremiumProfile | null): boolean {
  if (!profile) return false;

  return (
    profile.is_lifetime === true ||
    profile.is_beta_tester === true ||
    (profile.subscription_tier === 'premium' &&
      (!profile.premium_expires_at || isInFuture(profile.premium_expires_at))) ||
    isInFuture(profile.free_premium_until)
  );
}

/**
 * Tier eines Nutzers inkl. Ablaufpruefung und Partner-Freigabe.
 *
 * Gegenstueck: get_user_tier() in
 * supabase/migrations/160_swipe_limit_enforcement.sql. Die Freigabe wird wie
 * dort genau eine Ebene tief aufgeloest (keine Rekursion), und eine
 * Selbstreferenz zaehlt nicht als Premium.
 */
async function resolveUserTier(userId: string): Promise<'free' | 'premium'> {
  const { data: profile, error } = await supabase
    .from('profiles')
    .select(PREMIUM_FIELDS)
    .eq('id', userId)
    .maybeSingle();

  if (error) throw error;
  if (!profile) return 'free';

  const own = profile as unknown as PremiumProfile;
  if (hasOwnPremium(own)) return 'premium';

  const sharedFrom = own.premium_shared_from;
  if (sharedFrom && sharedFrom !== userId) {
    const { data: sharer, error: sharerError } = await supabase
      .from('profiles')
      .select(PREMIUM_FIELDS)
      .eq('id', sharedFrom)
      .maybeSingle();

    if (sharerError) throw sharerError;
    if (hasOwnPremium(sharer as unknown as PremiumProfile | null)) return 'premium';
  }

  return 'free';
}

interface UseLimitsReturn {
  limits: Record<string, number | boolean | string>;
  isLoading: boolean;
  error: Error | null;
  userTier: 'free' | 'premium';
  getLimit: <T extends number | boolean | string>(key: LimitKey) => T;
  isLimitEnabled: (key: LimitKey) => boolean;
  getNumberLimit: (key: LimitKey) => number;
  isUnlimited: (key: LimitKey) => boolean;
  refresh: () => Promise<void>;
}

// Cache for limits (5 minute TTL).
// An userId UND tier gebunden: frueher war das eine Modul-Variable ohne
// Nutzerbezug, sodass ein Free-User die Limits eines zuvor eingeloggten
// Premium-Users erben konnte.
let limitsCache: {
  userId: string;
  data: Record<string, number | boolean | string>;
  tier: 'free' | 'premium';
  timestamp: number;
} | null = null;

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Cache verwerfen. Muss bei jedem Wechsel der Identitaet oder des Tiers
 * aufgerufen werden: Login, Logout, Kauf, Wiederherstellung.
 */
export function clearLimitsCache(): void {
  limitsCache = null;
}

export function useLimits(): UseLimitsReturn {
  const [limits, setLimits] = useState<Record<string, number | boolean | string>>({});
  const [userTier, setUserTier] = useState<'free' | 'premium'>('free');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadLimits = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user?.id) {
        // Kein eingeloggter User: keine Limits, keine Cache-Nutzung
        setUserTier('free');
        setLimits({});
        setIsLoading(false);
        return;
      }

      // Tier IMMER vor dem Cache-Lookup ermitteln. Frueher lief der
      // Cache-Treffer davor, sodass ein Free-User den Tier des Vorgaengers
      // uebernommen hat.
      const tier = await resolveUserTier(user.id);
      setUserTier(tier);

      const now = Date.now();
      if (
        limitsCache &&
        limitsCache.userId === user.id &&
        limitsCache.tier === tier &&
        (now - limitsCache.timestamp) < CACHE_TTL
      ) {
        setLimits(limitsCache.data);
        setIsLoading(false);
        return;
      }

      // Determine which suffix to use for fetching limits
      const tierSuffix = tier === 'premium' ? 'premium' : 'free';

      // Build list of keys to fetch
      const keysToFetch = LIMIT_KEYS.map(key => `${key}_${tierSuffix}`);

      const { data, error: settingsError } = await supabase
        .from('system_settings')
        .select('key, value')
        .in('key', keysToFetch);

      if (settingsError) throw settingsError;

      // Build limits object with defaults
      const limitsMap: Record<string, number | boolean | string> = {};

      LIMIT_KEYS.forEach(key => {
        const settingKey = `${key}_${tierSuffix}`;
        const setting = data?.find(s => s.key === settingKey);
        const defaults = DEFAULT_LIMITS[key];
        const fallback = tierSuffix === 'premium' ? defaults.premium : defaults.free;

        if (setting?.value !== undefined && setting?.value !== null) {
          // Zahlen-Limits robust parsen: JSONB liefert je nach Herkunft
          // 25 oder "25", und "-1" als String wuerde die Pruefung auf
          // unbegrenzt (=== -1) sonst unterlaufen.
          limitsMap[key] =
            typeof fallback === 'number'
              ? parseSettingNumber(setting.value, fallback)
              : (setting.value as number | boolean | string);
        } else {
          limitsMap[key] = fallback;
        }
      });

      // Update cache
      limitsCache = {
        userId: user.id,
        data: limitsMap,
        tier,
        timestamp: now,
      };

      setLimits(limitsMap);
    } catch (err) {
      console.error('Error in useLimits:', err);
      setError(err instanceof Error ? err : new Error('Failed to load limits'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLimits();
  }, [loadLimits]);

  // Helper to get a limit value with proper typing
  const getLimit = useCallback(<T extends number | boolean | string>(key: LimitKey): T => {
    const value = limits[key];
    if (value !== undefined) {
      return value as T;
    }
    // Return default
    const tierSuffix = userTier === 'premium' ? 'premium' : 'free';
    const defaults = DEFAULT_LIMITS[key];
    return (tierSuffix === 'premium' ? defaults.premium : defaults.free) as T;
  }, [limits, userTier]);

  // Helper for boolean limits
  const isLimitEnabled = useCallback((key: LimitKey): boolean => {
    return getLimit<boolean>(key);
  }, [getLimit]);

  // Helper for number limits
  const getNumberLimit = useCallback((key: LimitKey): number => {
    return getLimit<number>(key);
  }, [getLimit]);

  // Helper to check if a number limit is unlimited (-1)
  const isUnlimited = useCallback((key: LimitKey): boolean => {
    const value = getLimit<number>(key);
    return value === -1;
  }, [getLimit]);

  const refresh = useCallback(async () => {
    clearLimitsCache();
    await loadLimits();
  }, [loadLimits]);

  return {
    limits,
    isLoading,
    error,
    userTier,
    getLimit,
    isLimitEnabled,
    getNumberLimit,
    isUnlimited,
    refresh,
  };
}

// Simple hook for checking a single boolean limit
export function useLimitEnabled(key: LimitKey): boolean | null {
  const { isLimitEnabled, isLoading } = useLimits();

  if (isLoading) return null;
  return isLimitEnabled(key);
}

// Simple hook for checking a number limit
export function useNumberLimit(key: LimitKey): { limit: number; isUnlimited: boolean; isLoading: boolean } {
  const { getNumberLimit, isUnlimited, isLoading } = useLimits();

  return {
    limit: isLoading ? 0 : getNumberLimit(key),
    isUnlimited: isLoading ? false : isUnlimited(key),
    isLoading,
  };
}
